/**
 * Executes async functions with a concurrency limit.
 * Maintains up to `limit` promises in-flight at any time for maximum throughput.
 *
 * @param {number} limit - Maximum number of concurrent promises
 * @param {Array} items - Array of items to process
 * @param {Function} iteratorFn - Async function to execute for each item, receives (item, index)
 * @returns {Promise<Array>} - Results in the same order as input items
 */
export async function asyncPool(limit, items, iteratorFn) {
  const results = [];
  const executing = new Set();

  let index = 0;
  for (const item of items) {
    const currentIndex = index++;
    const promise = Promise.resolve()
      .then(() => iteratorFn(item, currentIndex))
      .then((result) => {
        executing.delete(promise);
        return result;
      });

    results.push(promise);
    executing.add(promise);

    if (executing.size >= limit) {
      await Promise.race(executing);
    }
  }

  return Promise.all(results);
}
